/* eslint-disable @typescript-eslint/no-explicit-any */
import { ComponentProps, ComponentType, ForwardedRef, forwardRef } from 'react';

import { Tooltip, TooltipProps } from './Tooltip';

export interface WithToolTipProps {
  tooltipContent?: TooltipProps['content'];
  tooltipArrow?: TooltipProps['arrow'];
  tooltipPosition?: TooltipProps['position'];
  tooltipDelayMilSeconds?: TooltipProps['delayMilSeconds'];
  tooltipUnstyled?: TooltipProps['unstyled'];
  tooltipContentOffset?: TooltipProps['contentOffset'];
  tooltipClickable?: TooltipProps['clickable'];
  tooltipDisabled?: boolean;
}

export type WithTooltip<T> = T & WithToolTipProps;

interface CompositionConfig<T extends ComponentType<any>> {
  ensureTooltipPresence?: boolean;
  hasRef?: boolean;
  displayName?: string;

  defaultContentProp?: keyof ComponentProps<T>;
}

export function withToolTip<Comp extends ComponentType<any>, RefType>(
  component: Comp,
  { ensureTooltipPresence, hasRef, displayName, defaultContentProp }: CompositionConfig<Comp> = {},
): (props: ComponentProps<Comp> & WithToolTipProps) => JSX.Element {
  const WrapperWithToolTip = (
    {
      tooltipContent,
      tooltipArrow,
      tooltipPosition,
      tooltipDelayMilSeconds,
      tooltipUnstyled,
      tooltipContentOffset,
      tooltipDisabled,
      tooltipClickable,
      ...props
    }: ComponentProps<Comp> & WithToolTipProps,
    ref: ForwardedRef<RefType>,
  ): JSX.Element => {
    const Component = component as (props: any) => JSX.Element;

    const base = <Component ref={hasRef ? ref : undefined} {...props} />;

    const content =
      tooltipContent ??
      (defaultContentProp ? props[defaultContentProp as keyof typeof props] : null);

    if (!content) return <>{base}</>;

    return (
      <Tooltip
        content={content}
        arrow={tooltipArrow}
        position={tooltipPosition}
        delayMilSeconds={tooltipDelayMilSeconds}
        ensurePresence={ensureTooltipPresence}
        unstyled={tooltipUnstyled}
        contentOffset={tooltipContentOffset}
        disabled={tooltipDisabled}
        clickable={tooltipClickable}
      >
        {base}
      </Tooltip>
    );
  };

  WrapperWithToolTip.displayName = displayName;

  // ComponentProps<XX> already picks up the ref props, our wrap here is correct
  return (hasRef ? forwardRef(WrapperWithToolTip as any) : WrapperWithToolTip) as (
    props: ComponentProps<Comp> & WithToolTipProps,
  ) => JSX.Element;
}
